/**
 * useViewportVars — viewport 變數同步邏輯
 * (`--host-vw` / `--host-vh` / `--card-w` / `--card-h`)。
 *
 * # 為什麼存在
 *
 * `.responsive-card`(遊戲 iframe 容器)/ `.popup-frame`(modal 內容寬度)/
 * `.overlay-frame-clamp`(modal overlay 寬度)三處需要算出「跟 viewport 對齊
 * 又維持 9:16 cap」的寬高 px 值。原本寫成 CSS `clamp()` + `min()` + `var()`
 * 公式,但 Mi Browser / FB in-app WebView / 微信內嵌等 stripped Chromium 上
 * `var()` computed 不出來(Ionic issue #17781 同款 bug),導致 width 變 auto
 * → card 塌成 iframe 預設 300×150。
 *
 * 解法:**JS 直接算 px 值**,setProperty 進 `:root`,CSS 端只 `var()` consume。
 *
 * # 三層防禦
 *
 *   1. **`Math.max(visualViewport, innerWidth/Height)`** — 兩個 API 取較大者,
 *      某個 buggy 另一個救場
 *   2. **`initViewportVars()` 在 mount 前先跑一次**(在 main.ts 內 createApp 之前
 *      call) — 第一個 frame 就有值,避免 hydration race
 *   3. **`pollViewportVars()` 前 5 秒每 250ms 算一次**(catch Pixel / 小米 11
 *      URL bar 折疊不發 resize event 的情況)
 *   4. **`useViewportVarsListener()` 在 App.vue onMounted 註冊 resize /
 *      orientationchange / visualViewport.resize listener**(後續持續同步)
 *
 * # 公式分流
 *
 *   - **手機 (vw ≤ 768)**:**高填滿 viewport（上限 19.5:9）、寬維持 9:16 上限**(2026-09-09 / 09-22)。
 *      寬 = `min(100vw, host-vh × 9/16)` + sliver 容差,高 = `min(host-vh, 寬 × 2340/1080)`,inline px
 *      (繞 stripped Chromium var()/dvh bug)。詳見 applyViewportVars 內註解。
 *   - **PC (vw > 768)**:9:16,純 CSS `min(100vw, 100dvh × 9/16)` + `aspect-ratio: 9/16`,
 *      cardW ref = 0(不下 inline)→ 居中露 host bg
 */

import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue'

const MOBILE_MAX_WIDTH = 768
/**
 * Fast polling 前 5 秒(250ms 一次)抓 URL bar 折疊瞬間 visualViewport 還沒
 * 來得及 fire resize event 的場景。5 秒後停止 — 之後完全靠 resize / orientationchange /
 * visualViewport.resize event + ResizeObserver 同步,不再 polling。
 *
 * (原本還有永久 2s slow polling 防 Mi Browser stale value,但根因已從讀法上修
 * 掉 — applyViewportVars 內 vv 優先於 innerWidth → 不會再讀到「錯的 stale 值」,
 * polling 沒事可做,移除。)
 */
const FAST_POLL_INTERVAL_MS = 250
const FAST_POLL_DURATION_MS = 5000

/**
 * Sliver 容差:9:16 算出的寬只差 viewport 幾 px 時,直接吃滿 `width`。
 *
 * 由來 `83549d0`(2026-05-27 `fix: +5px tolerance in mobile card width to kill
 * iPhone Safari sliver`):iPhone Safari 顯示 URL bar 時 viewport ≈ 393×695,
 * `695 × 9/16 ≈ 390.94` 比 393 小 ~2px → 裸 `Math.min` 選 390.94,左右各露
 * ~1px 的 host bg 細線(肉眼可見)。5px 是放寬的安全值:iPhone 各 model +
 * URL bar 折疊中間狀態的誤差約 1~3px。
 *
 * 🛑 這個容差被弄丟過兩次:`e6faa7f` 把計算從 CSS 搬進 JS 時沒帶走,
 * `80430b5` 回鎖 9:16 時也沒補回 → 細線在線上躺了 3 個月(2026-09-09 實機重現)。
 * **改這段公式時不要再把它拿掉。**
 *
 * 寫成「差距 ≤ 容差就 snap 到 width」而不是原版的 `min(w, h×9/16 + 5px)`:
 * 後者會把**所有**比 9:16 胖的直向裝置一律加寬 5px(iPad mini 576 → 581),
 * 那是無謂位移;snap 版只在真的近乎相等時才吃滿,其餘完全不動。
 */
const SLIVER_TOLERANCE_PX = 5

/**
 * 卡片高度上限：`card-w × 2340 / 1080`（= 19.5:9）。
 *
 * 遊戲端的背景 / 場景素材最高只做到 **1080×2340**（Cocos 以 1080 寬為基準往上下延伸畫背景），
 * 比 19.5:9 更瘦長的機型（Pixel 10 412×924 = 2.243、iPhone 15 Pro Max 430×932 = 2.167 剛好到頂）
 * 若讓卡片吃滿 viewport，畫布上下會多出遊戲蓋不到的區域 → **黑邊**（2026-09-22 Pixel 10 實機：頂部 ~30px 黑）。
 * 有上限後那截改由 host 的模糊底圖補（`.game-wrapper` 兩軸置中），寬度公式不變。
 * 🛑 這個數字是遊戲端素材規格（遊戲端同事 2026-09-22 給的），不是 host 自己選的；遊戲素材改規格才動它。
 */
const MAX_CARD_ASPECT = 2340 / 1080

/**
 * Module-scope reactive refs — single source of truth for card dimensions。
 *
 * 為什麼也曝這個值給 App.vue 直接綁 inline style:
 *   Mi Browser / FB in-app WebView 等 stripped Chromium 上,即使 setProperty
 *   寫好 `--card-w`,CSS `width: var(--card-w, 100%)` 也可能 resolve 失敗 → layout 塌。
 *   把 cardW / cardH 也以 reactive ref 形式 export,App.vue 直接 `:style="{ width: cardW+'px' }"`
 *   inline 注入 → 跳過 var() resolve,任何瀏覽器都吃 inline px。
 *
 *   CSS var 同時保留(`.popup-frame` / `.overlay-frame-clamp` / `.frame-clamp` 還用),
 *   只是 `.responsive-card` 主路徑改吃 inline。
 *
 * 初始值在 module load 時跑一次 applyViewportVars(),避免 App.vue 第一次 render
 * 時 cardW.value = 0 造成的 0×0 flash。
 */
const cardW = ref(0)
const cardH = ref(0)

/**
 * 上次 apply 的值,dedup 用 — polling 讀到同 viewport 值就 skip,
 * 避免 setProperty / ref 重複更新造成不必要的 reactivity / repaint。
 *
 * 結果:正常情況(viewport 穩定),polling 100% no-op。Mi Browser stale value
 * 修正瞬間才會真的更新一次,之後再 polling 都 no-op。
 */
let lastW = -1
let lastH = -1

/**
 * 算一次 viewport vars 並寫到 `:root`。pure 函式(除了 DOM side-effect),
 * `initViewportVars` / polling / listener handler 都吃這隻。
 */
function applyViewportVars(): void {
  const root = document.documentElement
  const vv = window.visualViewport
  // 讀法優先序:
  //   1. visualViewport(如果存在且 > 0)— 真實「visible viewport」,跟使用者眼睛
  //      看到的螢幕可視區一致。Mi Browser 的關鍵 bug:`innerWidth` 報的是
  //      *layout* viewport(可能被瀏覽器縮放成 ~1.36× 大),visualViewport 才正確。
  //   2. innerWidth/Height — 沒 visualViewport API 的舊瀏覽器 fallback。
  //
  // 舊版用 `Math.max(vv, inner)` 取較大者是為了防 vv 初始給 0,但反而踩到
  // Mi Browser 的反向 bug(inner 大、vv 對)→ 改成 vv 優先 + 0 兜底。
  const vvW = vv?.width ?? 0
  const vvH = vv?.height ?? 0
  const width = vvW > 0 ? vvW : window.innerWidth
  const height = vvH > 0 ? vvH : window.innerHeight
  // Dedup:跟上次一樣就 skip,避免 setProperty / ref 重複更新觸發 reactivity / repaint。
  // 正常情況(viewport 穩定)polling 100% no-op,不會閃。
  if (width === lastW && height === lastH) return
  lastW = width
  lastH = height
  root.style.setProperty('--host-vw', `${width}px`)
  root.style.setProperty('--host-vh', `${height}px`)

  // Card 寬高分流:
  //   - PC (>768):純 CSS 在 style.css 用 `min(100vw, calc(100dvh*9/16))` +
  //     `aspect-ratio: 9/16` 算,**不靠 JS**。但 CSS var 仍 setProperty(`.popup-frame`
  //     / `.overlay-frame-clamp` 等 mobile + PC 都用),所以這裡還是要算 var 的值(同式)。
  //   - Mobile (≤768):JS 算 px 寫進 reactive ref,App.vue 綁 inline style,
  //     繞開 Mi Browser 等 stripped WebView 的 var() resolve bug。
  let nextCardW: number
  let nextCardH: number
  if (width <= MOBILE_MAX_WIDTH) {
    // 手機：**高填滿 viewport、寬維持 9:16 上限**（2026-09-09）。
    //
    // 為什麼只解鎖「高」：
    //   19.5:9 / 20:9 高瘦機（iPhone 14 Pro Max 430×932、Android 412×915）上,
    //   舊式 `nextCardH = nextCardW × 16/9` 會算出 764 → 上下各露 84px host 模糊底圖,
    //   遊戲背景頂/底被切掉（遊戲端 2026-09-09 回報,競品與直開遊戲都是滿版）。
    //   Cocos（designResolution 1080×1920）拿到全高容器後自己以 1080 寬為基準畫滿,
    //   多出來的上下由遊戲背景圖延伸覆蓋,實測無二次 letterbox。
    //
    // 🛑 為什麼**不**連寬也填滿（提案曾如此建議,不要照做）：
    //   `--card-w` 是「彈窗 / loading 框 / overlay 遮罩」四處共用的寬度來源
    //   （PopupWrapper `.popup-frame` / BalanceModal / style.css `.overlay-frame-clamp`
    //   / LoadingScreen `.loading-frame`）,它對齊的是 Cocos 內 UI 的邏輯寬
    //   （SHOW_ALL 下 = `min(W, H×9/16)`,與容器無關）。若讓卡片寬 > `--card-w`,
    //   `.popup-frame` 蓋不住整張卡片 —— 而它的 overlay 是 transparent、靠自身不透明
    //   遮住遊戲 → 彈窗左右會露出**還在動的遊戲畫面**。寬度維持同源就自動不會有這問題。
    //   同理也避開「≤768 但比 9:16 胖」的直向裝置（iPad mini 744×1133 / Fold 內屏 /
    //   桌機窄視窗）被撐成胖容器。
    //
    // 仍用 inline px（App.vue 綁），繞 stripped Chromium（Mi Browser / 微信內嵌等）的
    // var()/dvh resolve bug。不取整：保留小數寬，讓 card-w 與 cocos 彈窗寬度一致。
    const capped = (height * 9) / 16
    nextCardW = width - capped <= SLIVER_TOLERANCE_PX ? width : capped
    // 高填滿 viewport，但**不超過 19.5:9**（遊戲背景最高只做到 1080×2340，再高就是黑邊；見 MAX_CARD_ASPECT）
    nextCardH = Math.min(height, nextCardW * MAX_CARD_ASPECT)
    cardW.value = nextCardW
    cardH.value = nextCardH
  } else {
    // PC:9:16,`min(vw, vh×9/16)` — **與 style.css `.responsive-card` / rem 基準完全同式**
    // (2026-07-21 拿掉 360~712 clamp:CSS 端早已無 clamp,JS 端漏改造成 modal(--card-w 712)
    //  蓋不住 iframe(810 @2560×1440)的 drift;此值純給 CSS var 用,card 自己走純 CSS)。
    nextCardW = Math.min(width, (height * 9) / 16)
    nextCardH = (nextCardW * 16) / 9
    // PC ref 設 0 = 信號「不要下 inline」,App.vue 看到 0 就回空物件給 :style
    cardW.value = 0
    cardH.value = 0
  }
  // CSS var 兩個 path 都更新(給 .popup-frame / .overlay-frame-clamp 等用)
  root.style.setProperty('--card-w', `${nextCardW}px`)
  root.style.setProperty('--card-h', `${nextCardH}px`)
}

/**
 * mount **前**呼叫一次(在 main.ts 內,createApp 之前)。
 *
 *   1. 立刻算一次 → 第一個 render frame 就有值
 *   2. 前 5 秒每 250ms fast polling(抓 URL bar 折疊瞬間 vv 還沒 fire resize event 的場景)
 *   3. 5 秒後停止 polling — 之後完全靠 resize listener + ResizeObserver 同步
 *
 * 用 `void` return,呼叫端不關心 polling timer。
 */
export function initViewportVars(): void {
  applyViewportVars()

  // Fast polling 前 5 秒,跑滿後 clearInterval 停掉
  let fastAttempts = 0
  const fastPollId = setInterval(() => {
    applyViewportVars()
    if (++fastAttempts >= FAST_POLL_DURATION_MS / FAST_POLL_INTERVAL_MS) {
      clearInterval(fastPollId)
    }
  }, FAST_POLL_INTERVAL_MS)
}

/**
 * Vue composable — 在 `setup()` 內 call,自動註冊 resize / orientationchange /
 * visualViewport listener,onBeforeUnmount 自動 cleanup。
 *
 * 回傳 `cardW` / `cardH` reactive refs,App.vue 直接綁 `.responsive-card` 的 inline style,
 * 繞掉 CSS var() 在 Mi Browser 等 stripped Chromium 的 resolve bug。
 *
 * 用法(App.vue):
 *
 *   const { cardW, cardH } = useViewportVarsListener()
 *   // template:
 *   //   <div class="responsive-card" :style="{ width: cardW + 'px', height: cardH + 'px' }">
 *
 * 注意:**不**會自己跑 initial pre-mount + polling 邏輯(那段由 main.ts 的
 * `initViewportVars()` 負責),這隻只管 mount 之後的持續同步。
 */
export function useViewportVarsListener(): {
  cardW: Readonly<Ref<number>>
  cardH: Readonly<Ref<number>>
} {
  let resizeObserver: ResizeObserver | null = null

  onMounted(() => {
    applyViewportVars() // 防呆:萬一 init 跟 mount 之間又有變化
    window.addEventListener('resize', applyViewportVars)
    window.addEventListener('orientationchange', applyViewportVars)
    window.visualViewport?.addEventListener('resize', applyViewportVars)
    window.visualViewport?.addEventListener('scroll', applyViewportVars)

    // ResizeObserver 補位:Mi Browser 等不發 resize event 但 html 元素實際 size
    // 真的有變(URL bar 折疊 / IME 出現 / 狀態列變化)的場景,RO 會抓到。
    // 跟 resize listener 並行,哪個先發就先觸發 applyViewportVars。
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => applyViewportVars())
      resizeObserver.observe(document.documentElement)
    }
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', applyViewportVars)
    window.removeEventListener('orientationchange', applyViewportVars)
    window.visualViewport?.removeEventListener('resize', applyViewportVars)
    window.visualViewport?.removeEventListener('scroll', applyViewportVars)
    resizeObserver?.disconnect()
    resizeObserver = null
  })

  return { cardW, cardH }
}
